import { FileOutlined, FileFilled as FilledFileIcon } from "@ant-design/icons"
import moment from "moment"

export const getEventColor = (paymentStatus, bookingStatus) => {
  if (bookingStatus === 'canceled') return 'var(--color-surface3)'
  const statusBasedColor = {
    full_paid: 'var(--color-blue-surface)',
    outstanding: 'var(--color-outstanding)',
    pending: 'var(--color-surface3)'
  }
  return statusBasedColor[paymentStatus] ?? 'var(--color-surface3)'
}

export const getEventBorderColor = (paymentStatus) => {
  const statusBasedColor = {
    full_paid: '#c5d9fd',
    outstanding: '#f0d88a',
    pending: '#d4d4d4'
  }
  return statusBasedColor[paymentStatus] ?? '#d4d4d4'
}

export const getJournalIcon = (journalNoteDates, date) => {
  const hasJournal = journalNoteDates?.includes(date)
  return hasJournal ? (
    <FilledFileIcon style={{ color: "var(--color-primary)", fontSize: 16 }} />
  ) : (
    <FileOutlined style={{ color: "var(--color-primary)", fontSize: 16 }} />
  )
}

export const startTimeValidate = (t, endTime, currentValue) => {
  if (!currentValue || !endTime) return Promise.resolve()
  if (currentValue.isBefore(endTime)) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.start_time_must_be_before_end_time")))
}

export const endTimeValidate = (t, startTime, currentValue) => {
  if (!currentValue || !startTime) return Promise.resolve()
  if (currentValue.isAfter(startTime)) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.end_time_must_be_after_start_time")))
}

export const startDateValidate = (t, endDate, currentValue) => {
  if (!currentValue || !endDate) return Promise.resolve()
  // Allow same day or before
  if (currentValue.isBefore(endDate) || currentValue.isSame(endDate, "day")) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.start_date_must_be_before_end_date")))
}

export const endDateValidate = (t, startDate, currentValue) => {
  if (!currentValue || !startDate) return Promise.resolve()
  // Allow same day or after
  if (currentValue.isAfter(startDate) || currentValue.isSame(startDate, "day")) return Promise.resolve()
  return Promise.reject(new Error(t("calendar.schedule_time_off.end_date_must_be_after_start_date")))
}

export const formatDate = (date) => date?.format("YYYY-MM-DD")
export const formatTime = (time) => time?.format("hh:mm a")

export const capitalizeFirstLetter = (str) => str?.charAt(0)?.toUpperCase() + str?.slice(1)

export const parseTime = (t) => (t ? moment(t, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null)

export const overlaps = (aStart, aEnd, bStart, bEnd) => {
  if (!aStart || !aEnd || !bStart || !bEnd) return false
  return aStart.isBefore(bEnd) && aEnd.isAfter(bStart)
}

export const parseTimeOffRanges = (timeOffRanges = []) => {
  return timeOffRanges.map((range) => {
    const [startStr, endStr] = String(range).split(" - ")
    return { start: parseTime(startStr), end: parseTime(endStr) }
  })
}

// Common helpers for all views
export const DAY_KEY_FORMAT = "YYYY-MM-DD"
export const dayKey = (m) => (m ? m.format(DAY_KEY_FORMAT) : "")

// Build a per-day map of time off information
// Returns shape: { [dayKey]: { allDay: boolean, ranges: [{ start, end }] } }
export const normalizeTimeOffByDay = (calendarTimeOff = [], calendarDays = []) => {
  const map = {}
  calendarDays.forEach((day) => {
    const key = dayKey(day)
    const items = (calendarTimeOff || [])
      .filter((toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day"))
      .map((toff) => {
        const start = moment(toff.start_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])
        const end = moment(toff.end_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])
        return { ...toff, start, end }
      })
    map[key] = {
      allDay: items.some((toff) => Boolean(toff.all_day)),
      ranges: items.map(({ start, end }) => ({ start, end }))
    }
  })
  return map
}

// Determine if an event conflicts with time off for a given day
export const hasEventConflict = (startMoment, endMoment, timeOffForDay) => {
  if (!startMoment || !endMoment || !timeOffForDay) return false
  if (timeOffForDay.allDay) return true
  const ranges = timeOffForDay.ranges || []
  return ranges.some((r) => overlaps(startMoment, endMoment, r.start, r.end))
}
